/**
 * Ryan O'Dowd
 * 2022-07-07
 * © Copyright 2022 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import {
  Linking,
  Platform,
  View,
} from 'react-native';
import React, {
  useEffect,
  useMemo,
} from 'react';
import {
  fetchWhatsNew,
  setWhatsNewLastReadVersion,
} from '@utils/store/actions';
import EmptyScreen from '@components/common/EmptyScreen';
import FullScreenScrollView from '@components/common/FullScreenScrollView';
import Header from '@components/Header';
import NewVersion from '@components/NewVersion';
import NotesCard from './NotesCard';
import Popover from '@components/common/Popover';
import ScreenWrapper from '@containers/ScreenWrapper';
import Text from '@components/common/Text';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';
import utilities from '@utils/utilities';

const WhatsNew = () => {
  const dispatch = hooks.useTypedDispatch();
  const i18n = hooks.useI18n();
  const navigation = hooks.useNavigation();
  const styles = hooks.useStyles(getStyles);

  hooks.useBackButton(() => {
    navigation.goBack();
    return true;
  });

  const mostRecentVersion = hooks.useTypedSelector((state) => state.versionSupport.most_recent_version);
  const storeLink = Platform.OS === 'ios' ? globals.appStoreUrl : globals.playStoreUrl;
  const isNewVersionAvailable = utilities.isNewAppVersionWithReleaseNotesAvailable();

  const whatsNewNotes = hooks.useTypedSelector((state) => state.whatsNewNotes);
  const whatsNewNotesToDisplay = useMemo(() => {
    return whatsNewNotes.filter((n) => utilities.isSecondOlderOrSameAsFirst(globals.appVersionNumber, n.app_version)).sort((a, b) => new Date(b.release_date).getTime() - new Date(a.release_date).getTime());
  }, [whatsNewNotes]);

  useEffect(() => {
    dispatch(fetchWhatsNew());

    return () => {
      dispatch(setWhatsNewLastReadVersion(globals.appVersionNumber));
    };
  }, [dispatch]);

  const backIcon = <Header.BackIcon />;

  return (
    <ScreenWrapper>
      <Header
        leftItems={[backIcon]}
        title={i18n('whatsNew')}
      />
      <View style={styles.subHeaderWrapper}>
        <Popover.Wrapper
          disabled={!isNewVersionAvailable}
          popover={
            <Popover.Inner>
              <Popover.Text style={styles.popoverText}>{`v${mostRecentVersion} available`}</Popover.Text>{/* @TODO: i18n */}
            </Popover.Inner>
          }
          actions={[
            {
              text: i18n('updateNow'),
              onPress: () => Linking.openURL(storeLink),
            },
          ]}
        >
          <Text style={styles.versionNumberText}>v{globals.appVersionNumber}</Text>
          {isNewVersionAvailable && <View style={styles.versionBadge} />}
        </Popover.Wrapper>
      </View>
      <FullScreenScrollView
        contentContainerStyle={styles.mainListContainer}
        showsVerticalScrollIndicator={false}
      >
        {Platform.OS !== 'web' && <NewVersion />}
        {whatsNewNotesToDisplay.length > 0
          ? whatsNewNotesToDisplay.map((note) => <NotesCard key={note.app_version} {...note} />)
          : (
            <EmptyScreen.Wrapper>
              <EmptyScreen.Text>
                {i18n('couldNotFetch')}
              </EmptyScreen.Text>
            </EmptyScreen.Wrapper>
          )
        }
      </FullScreenScrollView>
    </ScreenWrapper>
  );
};

export default WhatsNew;
